import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * A marker: "In your bag", "Cash to collect", "Packed and waiting".
 *
 * Every tone takes an ICON as well as a colour. Colour alone is not an
 * indicator — a red pill and an amber one are the same pill to a third of
 * colour-blind readers, and identical in forced-colours mode. On a phone held
 * at arm's length in the sun they are the same pill to everybody.
 */
const badge = cva(
  [
    'inline-flex items-center gap-1.5 rounded-md border',
    'px-2 py-0.5 text-xs font-semibold leading-snug',
    'forced-outline',
  ],
  {
    variants: {
      tone: {
        neutral: 'border-line-strong bg-sunken text-ink-muted',
        brand: 'border-line-brand/30 bg-wash text-brand',
        success: 'border-success-line/60 bg-success-surface text-success-ink',
        warning: 'border-warning-line/60 bg-warning-surface text-warning-ink',
        danger: 'border-error-line/60 bg-error-surface text-error-ink',
        info: 'border-info-line/60 bg-info-surface text-info-ink',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'sm' },
  },
);

export type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badge> & { icon?: LucideIcon };

export function Badge({ className, tone, size, icon: Icon, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badge({ tone, size }), className)} {...props}>
      {Icon ? <Icon className="size-3.5 shrink-0" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}
